// Skin Lab: a trial run of the backend spec "генерация ассетов под тему
// билда" — load the Unity "Export for Backend" zip, type a theme, and every
// asset is redrawn by gpt-image-2 (background first, then the rest with the
// new background as the style reference), fitted to the exact size and
// keyed to transparency in the browser. Shows what each call took and cost;
// downloads the zip the Unity import takes and a contact sheet. Nothing is
// stored. Super admin only — every drawing is paid.
import { useMemo, useRef, useState } from 'react';
import { unzipSync, zipSync, strToU8 } from 'fflate';
import { Button } from '@/components/Button';
import { drawSkinImage } from '@/api/skin-lab';
import {
  parseSkinManifest, drawSize, buildSkinPrompt, isStyleAnchor, keyOutMagenta, clearOutsideSprites,
  usageCost, checkSkinOutput, DEFAULT_IMAGE_PRICES,
  type SkinManifest, type SkinAsset, type ImageUsage, type ImagePrices, type SkinCheck,
} from '@/lib/skin-lab';

const DEFAULT_THEME = 'Tiger Fortune — red lacquer, gold and jade, Chinese coins and auspicious clouds, a tiger as the lucky mascot.';
/** Longest side of the style reference sent with every non-background asset. */
const STYLE_REF_SIDE = 1024;

interface Loaded {
  name: string;
  manifest: SkinManifest;
  originals: Record<string, Blob>;
  originalUrls: Record<string, string>;
}

interface Row {
  status: 'drawing' | 'done' | 'error';
  prompt: string;
  size: string;
  startedAt: number;
  ms?: number;
  usage?: ImageUsage | null;
  png?: Blob;
  url?: string;
  check?: SkinCheck;
  error?: string;
}

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return [c, ctx];
}

function toBlob(c: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas export failed'))), 'image/png'));
}

/** Scale with halving steps first, so a big shrink does not alias. */
function scaleCanvas(src: CanvasImageSource & { width: number; height: number }, w: number, h: number): HTMLCanvasElement {
  let cur: CanvasImageSource & { width: number; height: number } = src;
  while (cur.width / 2 >= w && cur.height / 2 >= h) {
    const [c, ctx] = canvas(Math.round(cur.width / 2), Math.round(cur.height / 2));
    ctx.drawImage(cur, 0, 0, c.width, c.height);
    cur = c;
  }
  const [c, ctx] = canvas(w, h);
  ctx.drawImage(cur, 0, 0, w, h);
  return c;
}

function b64ToBlob(b64: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: 'image/png' });
}

/** The original stretched onto the drawing canvas; transparent parts become magenta. */
async function shapeReference(original: Blob, a: SkinAsset, w: number, h: number): Promise<Blob> {
  const bmp = await createImageBitmap(original);
  const [c, ctx] = canvas(w, h);
  if (a.alpha) { ctx.fillStyle = '#FF00FF'; ctx.fillRect(0, 0, w, h); }
  ctx.drawImage(scaleCanvas(bmp, w, h), 0, 0);
  return toBlob(c);
}

async function styleReference(png: Blob): Promise<Blob> {
  const bmp = await createImageBitmap(png);
  const k = Math.min(1, STYLE_REF_SIDE / Math.max(bmp.width, bmp.height));
  return toBlob(scaleCanvas(bmp, Math.round(bmp.width * k), Math.round(bmp.height * k)));
}

/** The drawing into the file the import takes: keyed, exact size, sprites kept in their rectangles. */
async function fitOutput(drawn: Blob, a: SkinAsset): Promise<{ png: Blob; check: SkinCheck }> {
  const bmp = await createImageBitmap(drawn);
  const [src, sctx] = canvas(bmp.width, bmp.height);
  sctx.drawImage(bmp, 0, 0);
  if (a.alpha) {
    const data = sctx.getImageData(0, 0, src.width, src.height);
    keyOutMagenta(data.data);
    sctx.putImageData(data, 0, 0);
  }
  const scaled = scaleCanvas(src, a.width, a.height);
  const [out, ctx] = canvas(a.width, a.height);
  if (!a.alpha) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, a.width, a.height); }
  ctx.drawImage(scaled, 0, 0);
  const data = ctx.getImageData(0, 0, a.width, a.height);
  if (a.alpha && a.sprites.length > 1) {
    clearOutsideSprites(data.data, a.width, a.height, a.sprites);
    ctx.putImageData(data, 0, 0);
  }
  return { png: await toBlob(out), check: checkSkinOutput(a, out.width, out.height, data.data) };
}

function checker(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  const s = 8;
  for (let yy = 0; yy < h; yy += s) {
    for (let xx = 0; xx < w; xx += s) {
      ctx.fillStyle = ((xx + yy) / s) % 2 ? '#d4d4d4' : '#f4f4f4';
      ctx.fillRect(x + xx, y + yy, Math.min(s, w - xx), Math.min(s, h - yy));
    }
  }
}

/** Every asset as original | new, four pairs a row. */
async function contactSheet(assets: SkinAsset[], originals: Record<string, Blob>, rows: Record<string, Row>, title: string): Promise<Blob> {
  const CELL_W = 260, CELL_H = 200, LABEL = 26, GAP = 16, COLS = 4, HEAD = 56;
  const pairW = CELL_W * 2 + 8;
  const rowsN = Math.ceil(assets.length / COLS);
  const [c, ctx] = canvas(GAP + COLS * (pairW + GAP), HEAD + rowsN * (CELL_H + LABEL + GAP) + GAP);
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
  ctx.fillStyle = '#111'; ctx.font = 'bold 22px sans-serif';
  ctx.fillText(title, GAP, 36);
  const fit = async (blob: Blob | undefined, x: number, y: number) => {
    checker(ctx, x, y, CELL_W, CELL_H);
    if (!blob) return;
    const bmp = await createImageBitmap(blob);
    const k = Math.min(CELL_W / bmp.width, CELL_H / bmp.height);
    const w = Math.max(1, Math.round(bmp.width * k)), h = Math.max(1, Math.round(bmp.height * k));
    ctx.drawImage(scaleCanvas(bmp, w, h), x + (CELL_W - w) / 2, y + (CELL_H - h) / 2);
  };
  for (let i = 0; i < assets.length; i++) {
    const a = assets[i]!;
    const x = GAP + (i % COLS) * (pairW + GAP);
    const y = HEAD + Math.floor(i / COLS) * (CELL_H + LABEL + GAP);
    ctx.fillStyle = '#111'; ctx.font = '14px sans-serif';
    ctx.fillText(`${a.id}  ${a.width}×${a.height}`, x, y + 17);
    await fit(originals[a.original], x, y + LABEL);
    await fit(rows[a.id]?.png, x + CELL_W + 8, y + LABEL);
  }
  return toBlob(c);
}

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = name; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const usd = (v: number | null | undefined) => (v == null ? '—' : `$${v.toFixed(3)}`);
const secs = (ms: number | undefined) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)} s`);

export function SkinLab() {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState('');
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [quality, setQuality] = useState('medium');
  const [maxMp, setMaxMp] = useState(2.4);
  const [concurrency, setConcurrency] = useState(4);
  const [prices, setPrices] = useState<ImagePrices>(DEFAULT_IMAGE_PRICES);
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [running, setRunning] = useState(false);
  const [wall, setWall] = useState<{ start: number; end?: number } | null>(null);
  const stopRef = useRef(false);
  const rowsRef = useRef<Record<string, Row>>({});

  const update = (id: string, patch: Partial<Row> | null) => {
    const next = { ...rowsRef.current };
    if (patch === null) delete next[id];
    else next[id] = { ...(next[id] as Row), ...patch };
    rowsRef.current = next;
    setRows(next);
  };

  const onZip = async (file: File | undefined) => {
    setLoadError('');
    if (!file) return;
    try {
      const files = unzipSync(new Uint8Array(await file.arrayBuffer()));
      const manifestPath = Object.keys(files).find((p) => p === 'manifest.xml' || p.endsWith('/manifest.xml'));
      if (!manifestPath) throw new Error('no manifest.xml in the zip');
      const base = manifestPath.slice(0, manifestPath.length - 'manifest.xml'.length);
      const manifest = parseSkinManifest(new TextDecoder().decode(files[manifestPath]));
      const originals: Record<string, Blob> = {};
      const originalUrls: Record<string, string> = {};
      for (const a of manifest.assets) {
        const bytes = files[base + a.original];
        if (!bytes) continue;
        originals[a.original] = new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'image/png' });
        originalUrls[a.original] = URL.createObjectURL(originals[a.original]!);
      }
      Object.values(rowsRef.current).forEach((r) => r.url && URL.revokeObjectURL(r.url));
      rowsRef.current = {};
      setRows({});
      setWall(null);
      setLoaded({ name: file.name, manifest, originals, originalUrls });
    } catch (e) {
      setLoadError((e as Error).message);
    }
  };

  const drawOne = async (a: SkinAsset, style: Blob | null): Promise<Blob | null> => {
    if (!loaded) return null;
    const original = loaded.originals[a.original];
    const size = drawSize(a.width, a.height, maxMp * 1_000_000);
    const prompt = buildSkinPrompt(loaded.manifest, a, theme, { withStyleRef: !!style, stretched: size.stretched });
    const sizeStr = `${size.width}x${size.height}`;
    const old = rowsRef.current[a.id];
    if (old?.url) URL.revokeObjectURL(old.url);
    update(a.id, { status: 'drawing', prompt, size: sizeStr, startedAt: Date.now(), ms: undefined, usage: undefined, png: undefined, url: undefined, check: undefined, error: undefined });
    try {
      if (!original) throw new Error(`${a.original} is missing from the zip`);
      const images = [await shapeReference(original, a, size.width, size.height)];
      if (style) images.push(style);
      const res = await drawSkinImage({ prompt, size: sizeStr, quality, label: a.id, images });
      const { png, check } = await fitOutput(b64ToBlob(res.image), a);
      update(a.id, { status: 'done', ms: res.ms, usage: res.usage, png, url: URL.createObjectURL(png), check });
      return png;
    } catch (e) {
      update(a.id, { status: 'error', error: (e as Error).message, ms: Date.now() - (rowsRef.current[a.id]?.startedAt ?? Date.now()) });
      return null;
    }
  };

  const styleFromRows = async (): Promise<Blob | null> => {
    const anchor = loaded?.manifest.assets.find(isStyleAnchor);
    const png = anchor ? rowsRef.current[anchor.id]?.png : undefined;
    return png ? styleReference(png) : null;
  };

  const runAll = async () => {
    if (!loaded) return;
    stopRef.current = false;
    setRunning(true);
    setWall({ start: Date.now() });
    try {
      const anchor = loaded.manifest.assets.find(isStyleAnchor);
      if (anchor) await drawOne(anchor, null);
      const style = await styleFromRows();
      const queue = loaded.manifest.assets.filter((a) => a !== anchor);
      const worker = async () => {
        for (let a = queue.shift(); a && !stopRef.current; a = queue.shift()) await drawOne(a, style);
      };
      await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));
    } finally {
      setWall((w) => (w ? { ...w, end: Date.now() } : w));
      setRunning(false);
    }
  };

  const redraw = async (a: SkinAsset) => {
    await drawOne(a, isStyleAnchor(a) ? null : await styleFromRows());
  };

  const assets = loaded?.manifest.assets ?? [];
  const totals = useMemo(() => {
    let cost = 0, priced = 0, ms = 0, input = 0, output = 0, done = 0, failed = 0;
    for (const r of Object.values(rows)) {
      if (r.status === 'done') done++;
      if (r.status === 'error') failed++;
      if (r.status !== 'drawing' && r.ms) ms += r.ms;
      const c = usageCost(r.usage ?? null, prices);
      if (c != null) { cost += c; priced++; }
      input += r.usage?.input_tokens ?? 0;
      output += r.usage?.output_tokens ?? 0;
    }
    return { cost, priced, ms, input, output, done, failed };
  }, [rows, prices]);

  const themeSlug = theme.split(/[—\-,.]/)[0]!.trim().replace(/[^A-Za-z0-9]+/g, '') || 'Theme';
  const outName = `${themeSlug}_${loaded?.manifest.game.split('-')[0] || 'game'}_${loaded?.manifest.screen || 'lobby'}-top${assets.length}`;

  const downloadZip = async () => {
    const files: Record<string, Uint8Array> = {};
    const prompts: Record<string, unknown> = {};
    for (const a of assets) {
      const r = rows[a.id];
      if (r?.png) files[`${a.id}.png`] = new Uint8Array(await r.png.arrayBuffer());
      if (r) prompts[a.id] = { prompt: r.prompt, model: 'gpt-image-2', size: r.size, quality, ms: r.ms, usage: r.usage, seed: null };
    }
    files['prompts.json'] = strToU8(JSON.stringify({ theme, prompts }, null, 2));
    download(new Blob([zipSync(files, { level: 0 }) as Uint8Array<ArrayBuffer>], { type: 'application/zip' }), `${outName}.zip`);
  };

  const downloadSheet = async () => {
    if (!loaded) return;
    download(await contactSheet(assets, loaded.originals, rows, `${theme}  ·  gpt-image-2 ${quality}`), `${outName}_contact-sheet.png`);
  };

  const anyDone = Object.values(rows).some((r) => r.png);
  const priceInput = (key: keyof ImagePrices, label: string) => (
    <label className="text-xs text-content-subtle flex flex-col gap-1">
      {label}
      <input
        type="number" step="0.5" min="0" value={prices[key]}
        onChange={(e) => setPrices({ ...prices, [key]: Number(e.target.value) || 0 })}
        className="w-24 px-2 py-1 border border-border-strong rounded-md text-sm bg-surface text-content"
      />
    </label>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Skin Lab</h1>
        <p className="text-sm text-content-subtle mt-1">
          Trial run of theme assets: load the Unity &laquo;Export for Backend&raquo; zip, set a theme, and gpt-image-2 redraws every image with our OpenAI key.
          The background is drawn first and then sent as the style reference with every other image. Nothing is saved; every drawing is billed.
        </p>
      </div>

      <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input type="file" accept=".zip,application/zip" onChange={(e) => onZip(e.target.files?.[0])} className="text-sm" disabled={running} />
          {loaded && (
            <span className="text-sm text-content-subtle">
              {loaded.name}: {loaded.manifest.game} · {loaded.manifest.scope} · {assets.length} assets
              {loaded.manifest.currentStyle && <> · current style &laquo;{loaded.manifest.currentStyle}&raquo;</>}
            </span>
          )}
        </div>
        {loadError && <p className="text-danger text-sm">{loadError}</p>}

        <label className="block text-sm">
          <span className="text-content-muted">Theme prompt</span>
          <textarea
            value={theme} onChange={(e) => setTheme(e.target.value)} rows={3} disabled={running}
            className="mt-1 w-full px-3 py-2 border border-border-strong rounded-md text-sm bg-surface text-content"
          />
        </label>

        <div className="flex flex-wrap items-end gap-4">
          <label className="text-xs text-content-subtle flex flex-col gap-1">
            Quality
            <select value={quality} onChange={(e) => setQuality(e.target.value)} disabled={running}
              className="px-2 py-1 border border-border-strong rounded-md text-sm bg-surface text-content">
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </label>
          <label className="text-xs text-content-subtle flex flex-col gap-1">
            Max draw size, MP
            <input type="number" step="0.1" min="0.66" max="8.29" value={maxMp} disabled={running}
              onChange={(e) => setMaxMp(Number(e.target.value) || 1)}
              className="w-24 px-2 py-1 border border-border-strong rounded-md text-sm bg-surface text-content" />
          </label>
          <label className="text-xs text-content-subtle flex flex-col gap-1">
            In parallel
            <input type="number" min="1" max="10" value={concurrency} disabled={running}
              onChange={(e) => setConcurrency(Math.min(10, Math.max(1, Number(e.target.value) || 1)))}
              className="w-20 px-2 py-1 border border-border-strong rounded-md text-sm bg-surface text-content" />
          </label>
          {priceInput('textIn', '$/1M text in')}
          {priceInput('imageIn', '$/1M image in')}
          {priceInput('out', '$/1M image out')}
          <div className="flex gap-2 ml-auto">
            {running
              ? <Button variant="secondary" onClick={() => { stopRef.current = true; }}>Stop after current</Button>
              : <Button onClick={runAll} disabled={!loaded || !theme.trim()}>Run all {assets.length || ''}</Button>}
          </div>
        </div>
        <p className="text-xs text-content-subtle">
          Every image is drawn at its own aspect (at most 3:1 — thinner bars are drawn squashed and stretched back) and between 0.66 MP and the cap above, then fitted to the exact size in the browser.
          Transparent images are drawn on magenta and keyed out. Prices are per 1M tokens from OpenAI&apos;s usage; check them against the OpenAI pricing page.
        </p>
      </section>

      {Object.keys(rows).length > 0 && (
        <section className="bg-surface border border-border rounded-lg p-4 flex flex-wrap gap-x-8 gap-y-2 items-center text-sm">
          <span><b>{totals.done}</b> done{totals.failed ? <>, <b className="text-danger">{totals.failed}</b> failed</> : null} of {assets.length}</span>
          <span>Cost <b>{usd(totals.priced ? totals.cost : null)}</b>{totals.priced < totals.done ? ` (${totals.done - totals.priced} without usage)` : ''}</span>
          <span>Wall time <b>{wall ? secs((wall.end ?? Date.now()) - wall.start) : '—'}</b>{running && ' …'}</span>
          <span>Sum of calls <b>{secs(totals.ms)}</b></span>
          <span className="text-content-subtle">Tokens in {totals.input.toLocaleString()} · out {totals.output.toLocaleString()}</span>
          <div className="flex gap-2 ml-auto">
            <Button variant="secondary" size="sm" onClick={downloadSheet} disabled={!anyDone}>Contact sheet</Button>
            <Button size="sm" onClick={downloadZip} disabled={!anyDone}>Download zip</Button>
          </div>
        </section>
      )}

      {loaded && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {assets.map((a) => {
            const r = rows[a.id];
            const cost = usageCost(r?.usage ?? null, prices);
            return (
              <div key={a.id} className="bg-surface border border-border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-content truncate">{a.id}</div>
                    <div className="text-xs text-content-subtle">
                      {a.category} · {a.width}×{a.height} · {a.alpha ? 'transparent' : 'opaque'}
                      {a.sprites.length > 1 && ` · sheet of ${a.sprites.length}`}
                      {r?.size && ` · drawn ${r.size.replace('x', '×')}`}
                    </div>
                  </div>
                  <Button size="sm" variant="secondary" disabled={running || r?.status === 'drawing' || !theme.trim()} onClick={() => redraw(a)}>
                    {r ? 'Redraw' : 'Draw'}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[loaded.originalUrls[a.original], r?.url].map((src, i) => (
                    <div key={i} className="h-40 rounded border border-border flex items-center justify-center overflow-hidden"
                      style={{ backgroundImage: 'repeating-conic-gradient(#d4d4d4 0% 25%, #f4f4f4 0% 50%)', backgroundSize: '16px 16px' }}>
                      {src
                        ? <img src={src} alt={i ? 'new' : 'original'} className="max-h-full max-w-full object-contain" />
                        : <span className="text-xs text-neutral-500">{i === 0 ? 'missing' : r?.status === 'drawing' ? 'drawing…' : r?.status === 'error' ? 'failed' : '—'}</span>}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-subtle">
                  <span>{r?.status === 'drawing' ? 'drawing…' : secs(r?.ms)}</span>
                  <span>{usd(cost)}</span>
                  {r?.usage && <span>in {r.usage.input_tokens ?? '?'} · out {r.usage.output_tokens ?? '?'} tokens</span>}
                  {r?.check && (r.check.ok
                    ? <span className="text-success">checks ok</span>
                    : <span className="text-danger">{r.check.problems.join('; ')}</span>)}
                </div>
                {r?.error && <p className="text-xs text-danger break-words">{r.error}</p>}
                {r?.prompt && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-content-subtle">Prompt</summary>
                    <pre className="mt-1 whitespace-pre-wrap text-content-muted">{r.prompt}</pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
